// ─── src/data/italy/regions.js ─────────────────────────────────────────────────
// Italy region entities, primary, secondary, and legacy.
// Schema: id, countrySlug, slug, name, priorityLevel, listingCount,
//         urlEnabledManual, urlEverActivated, canonicalRoute,
//         description, focusKeywords, aiSummary, intentSignals, entityAliases,
//         isLegacy, legacyNote (legacy regions only).
//
// No duplicate representation: Rome is a region, not a city under another region.
// Legacy regions (Veneto, Lombardy, Lazio, Campania) are imported-data artefacts,
// kept as disabled secondaries for traceability.

export const ITALY_REGIONS = [
  // ═══════════════════════════════════════════════════════════════════════════
  // PRIMARY REGIONS
  // ═══════════════════════════════════════════════════════════════════════════

  {
    id: "amalfi-coast", countrySlug: "italy", slug: "amalfi-coast", name: "Amalfi Coast",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: true, listingCount: 6,
    lat: 40.634, lng: 14.602,
    canonicalRoute: "/italy/amalfi-coast",
    description: "Few places carry the romance of the Amalfi Coast. This UNESCO-protected coastline has drawn artists and travellers for centuries, and today it draws couples seeking a setting impossible to replicate anywhere else. Ravello, perched high above the Tyrrhenian Sea, is the natural home of grand estate weddings; Villa Cimbrone and Hotel Caruso have hosted celebrations of legendary beauty. Positano cascades down the hillside in layers of whitewashed colour, offering intimacy and discovery. Amalfi town, framed by a cathedral square and medieval architecture, is rooted in local history and culture.\n\nBeyond the towns, hidden gardens, secluded estates and clifftop villas reveal themselves along the wider coastline, places where the views are uninterrupted and the setting speaks for itself. The light at golden hour, the scent of lemon groves, the sound of the sea far below, these details cannot be designed. Weddings here range from intimate ceremonies on private terraces to formal celebrations in historic palaces, shaped by a culinary tradition guests remember long after the day itself.",
    seoText: "The Amalfi Coast is one of the world's most celebrated wedding destinations, drawing couples to its dramatic clifftop landscapes, turquoise waters and sun-bleached villages.",
    focusKeywords: ["amalfi coast wedding", "ravello wedding venue", "positano wedding"],
    aiSummary: "Top 3 Italian region by search volume. Ravello dominates luxury tier. Peak season May-October.",
    intentSignals: {
      high: ["ravello wedding venue book", "amalfi coast wedding planner"],
      mid: ["best amalfi coast wedding venues", "positano wedding ideas"],
      low: ["amalfi coast wedding inspiration"],
    },
    entityAliases: ["Costiera Amalfitana", "Amalfi", "Divina Costiera"],
    ai: {
      positioning: "Vertical drama. Cliffs and sea and a blue so vivid it doesn't look real.",
      opening_line: "The Amalfi Coast isn't a backdrop, it's a co-author. The sea, the cliffs, the light at 6pm when everything goes gold.",
      differentiation: "Ravello sits 350 metres above the sea and delivers an intimacy that Positano, for all its beauty, cannot match. Less photographed, more private.",
      register: "dramatic-coastal",
      style_affinity: ["coastal-dramatic", "romantic", "historic-ceremony", "intimate"],
      season_peak: ["may", "june", "september", "october"],
      season_avoid: ["july", "august"],
      scale_fit: "medium",
      not_for: ["rustic-farmhouse", "vineyard", "urban-industrial"],
      price_index: "ultra",
      cross_sell: ["sicily", "puglia", "tuscany"],
    },
  },
  {
    id: "tuscany", countrySlug: "italy", slug: "tuscany", name: "Tuscany",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: true, listingCount: 8,
    lat: 43.769, lng: 11.256,
    canonicalRoute: "/italy/tuscany",
    heroSubtitle: "Villa estates, vineyard receptions, hilltop ceremonies. Tuscany remains the world's most sought-after destination for a luxury Italian wedding.",
    description: "Rolling vineyards, medieval hilltop towns and Renaissance villas. The quintessential Italian wedding destination.",
    focusKeywords: ["tuscany wedding", "tuscan villa wedding", "wedding venues tuscany"],
    aiSummary: "Highest listing density in Italy. Villa weddings dominate. Strong year-round demand with Q2/Q3 peak.",
    intentSignals: {
      high: ["tuscan villa wedding venue hire", "tuscany wedding planner cost"],
      mid: ["best wedding venues tuscany", "chianti wedding ideas"],
      low: ["tuscan wedding traditions"],
    },
    entityAliases: ["Toscana"],
    ai: {
      positioning: "Rolling hills, cypress trees, and golden light that makes every photograph look like a painting.",
      opening_line: "Tuscany in September is something else, the light turns amber at 4pm and the cypress trees throw shadows across the villas.",
      differentiation: "The difference between Val d'Orcia and Chianti is 20 minutes and 40% fewer tourists. Same light, more privacy.",
      register: "romantic-grand",
      style_affinity: ["rustic-luxe", "romantic", "vineyard", "historic", "intimate"],
      season_peak: ["may", "june", "september", "october"],
      season_avoid: ["july", "august"],
      scale_fit: "all",
      not_for: ["ultra-modern", "beach-coastal", "urban-industrial"],
      price_index: "premium",
      cross_sell: ["provence", "umbria", "lake-como"],
    },
  },
  {
    id: "lake-como", countrySlug: "italy", slug: "lake-como", name: "Lake Como",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: true, listingCount: 5,
    lat: 45.982, lng: 9.261,
    canonicalRoute: "/italy/lake-como",
    description: "Belle Époque villas lining Italy's most glamorous lake. The pinnacle of destination wedding prestige.",
    focusKeywords: ["lake como wedding", "villa wedding lake como", "como wedding venue"],
    aiSummary: "Highest average spend per wedding globally. Villa Balbiano effect drives demand. Capacity-constrained market.",
    intentSignals: {
      high: ["lake como villa wedding book", "lake como wedding planner hire"],
      mid: ["best lake como wedding venues", "lake como wedding cost guide"],
      low: ["lake como wedding inspiration"],
    },
    entityAliases: ["Lago di Como", "Lario", "Como"],
    ai: {
      positioning: "Where old-money elegance meets mountain water, intimate lakeside villas and Europe's most dramatic setting.",
      opening_line: "Lake Como is one of those places that makes couples feel they've arrived somewhere that was made for exactly this occasion.",
      differentiation: "Como versus Garda: Como is quieter, more refined, and significantly more expensive. Garda has scale; Como has prestige.",
      register: "romantic-grand",
      style_affinity: ["lakeside", "romantic", "historic-ceremony", "urban-prestige"],
      season_peak: ["may", "june", "september"],
      season_avoid: ["november", "december", "january", "february"],
      scale_fit: "intimate",
      not_for: ["rustic-farmhouse", "vineyard", "beach-coastal"],
      price_index: "ultra",
      cross_sell: ["tuscany", "venice", "lake-garda"],
    },
  },
  {
    id: "sicily", countrySlug: "italy", slug: "sicily", name: "Sicily",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: false, listingCount: 3,
    lat: 37.853, lng: 15.289,
    canonicalRoute: "/italy/sicily",
    description: "Ancient Greek temples, baroque palazzi and volcanic landscapes. Raw, romantic and increasingly sought-after.",
    focusKeywords: ["sicily wedding", "taormina wedding venue", "sicilian wedding"],
    aiSummary: "Emerging luxury destination. Taormina leads demand. Lower price point attracts value-conscious luxury couples.",
    intentSignals: {
      high: ["taormina wedding venue book", "sicily wedding planner"],
      mid: ["best sicily wedding venues"],
      low: ["sicily destination wedding"],
    },
    entityAliases: ["Sicilia"],
    ai: {
      positioning: "Ancient Greek temples, baroque palazzi, and a light so sharp it makes everything look like a film still.",
      opening_line: "Sicily has been making people feel they've discovered something others haven't found yet, for about two thousand years.",
      differentiation: "Sicily versus Puglia: Sicily has more history, more drama, more extremes. Puglia is gentler, more pastoral, slightly easier. Sicily rewards couples who want to feel they've done something brave.",
      register: "historic-ceremonial",
      style_affinity: ["historic-ceremony", "coastal-dramatic", "rustic-luxe", "romantic"],
      season_peak: ["may", "june", "september", "october"],
      season_avoid: ["july", "august"],
      scale_fit: "all",
      not_for: ["ultra-modern", "vineyard", "urban-prestige"],
      price_index: "mid",
      cross_sell: ["puglia", "amalfi-coast", "rome"],
    },
  },
  {
    id: "puglia", countrySlug: "italy", slug: "puglia", name: "Puglia",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: false, listingCount: 4,
    lat: 40.352, lng: 18.174,
    canonicalRoute: "/italy/puglia",
    heroSubtitle: "Experience the rustic charm and Mediterranean beauty of Southern Italy's most enchanting wedding destination. Masserie, trulli villages, and whitewashed coastal towns bring authentic Italian elegance to your celebration.",
    description: "Puglia (Apulia), Southern Italy's fastest-growing luxury wedding destination. Masseria estates in olive groves, whitewashed trulli villages, Baroque Lecce, and pristine coastlines offer authentic Italian charm at exceptional value. From Salento's romance to the Itria Valley's countryside, experience world-class cuisine, stunning natural light, and centuries of tradition.",
    focusKeywords: ["puglia wedding", "masseria wedding venue", "lecce wedding", "salento wedding", "trulli wedding", "apulia wedding", "puglia destination wedding", "itria valley wedding", "puglia wedding planner", "masserie wedding italy"],
    aiSummary: "Fastest-growing Italian luxury wedding destination. Masseria estates and authentic farm venues drive demand. Strong food-and-wine culture differentiator. More affordable than Tuscany/Amalfi. Peak seasons May-June and September-October. Rising search volume from US/UK luxury couples seeking authentic Italian charm with exceptional value.",
    intentSignals: {
      high: ["masseria wedding puglia book", "lecce wedding venue", "puglia wedding planner hire", "salento wedding destination"],
      mid: ["best puglia wedding venues", "trulli wedding italy", "puglia wedding cost", "masseria wedding italy"],
      low: ["puglia wedding inspiration", "getting married in puglia", "authentic italian wedding"],
    },
    entityAliases: ["Apulia", "Le Puglie", "Salento", "Itria Valley", "Valle d'Itria", "Land of Masserie"],
    ai: {
      positioning: "Masseria estates in ancient olive groves, trulli villages, and a sun-bleached southern light that feels older than anywhere else in Italy.",
      opening_line: "Puglia has been discovered but not overrun. The masseria estates are genuinely old, the olive trees are genuinely ancient, and the light in August has a quality that makes everything look like film.",
      differentiation: "Puglia versus Sicily: Sicily has more drama and more history. Puglia is more pastoral, more private, and significantly better value. Couples who want to feel they've found something without having to prove they're brave choose Puglia.",
      register: "sun-social",
      style_affinity: ["rustic-luxe", "vineyard", "sun-social", "romantic", "intimate"],
      season_peak: ["may", "june", "september", "october"],
      season_avoid: ["july", "august"],
      scale_fit: "all",
      not_for: ["ultra-modern", "urban-prestige", "coastal-dramatic", "wild-remote"],
      price_index: "mid",
      cross_sell: ["sicily", "amalfi-coast", "sardinia"],
    },
  },
  {
    id: "rome", countrySlug: "italy", slug: "rome", name: "Rome",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    lat: 41.903, lng: 12.496,
    canonicalRoute: "/italy/rome",
    description: "The Eternal City, ancient ruins, baroque palazzi and a cinematic grandeur that no other city on earth can match.",
    focusKeywords: ["rome wedding", "rome wedding venue", "luxury wedding rome"],
    aiSummary: "Urban luxury hub. Palazzo and hotel venues dominate. Strong elopement and intimate ceremony demand.",
    intentSignals: {
      high: ["rome luxury wedding venue book", "rome wedding planner hire"],
      mid: ["best rome wedding venues", "rome destination wedding"],
      low: ["rome wedding ideas", "getting married in rome"],
    },
    entityAliases: ["Roma", "The Eternal City", "Città Eterna"],
    ai: {
      positioning: "The only city where the ruins are the backdrop, not the attraction. Ancient light, papal grandeur, and a wedding scene two thousand years in the making.",
      opening_line: "Rome assumes a lot of couples. It assumes they can hold their own in a setting with two thousand years of history behind every doorway.",
      differentiation: "Rome versus Venice: Venice is more romantic in the conventional sense. Rome has more weight. Couples who want to feel part of something enormous choose Rome. Couples who want to feel transported choose Venice.",
      register: "historic-ceremonial",
      style_affinity: ["historic-ceremony", "urban-prestige", "romantic", "understated-elegant"],
      season_peak: ["april", "may", "september", "october"],
      season_avoid: ["july", "august"],
      scale_fit: "grand",
      not_for: ["rustic-farmhouse", "vineyard", "wild-remote", "coastal-dramatic"],
      price_index: "premium",
      cross_sell: ["venice", "tuscany", "sicily"],
    },
  },
  {
    id: "venice", countrySlug: "italy", slug: "venice", name: "Venice",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: false, listingCount: 2,
    lat: 45.441, lng: 12.316,
    canonicalRoute: "/italy/venice",
    description: "Grand Canal palazzi, gondola arrivals and frescoed ballrooms. The world's most romantic city, utterly unmatched.",
    focusKeywords: ["venice wedding", "venice wedding venue", "palazzo wedding venice"],
    aiSummary: "Ultra-luxury niche. Palazzo Grand Canal venues command highest premiums. Gondola arrival is signature experience.",
    intentSignals: {
      high: ["venice palazzo wedding venue book", "venice wedding planner"],
      mid: ["best venice wedding venues", "venice destination wedding"],
      low: ["venice wedding inspiration"],
    },
    entityAliases: ["Venezia", "La Serenissima"],
    ai: {
      positioning: "The only major city built entirely on water, intimate in scale, otherworldly in atmosphere, impossible to replicate anywhere else.",
      opening_line: "Venice works because it removes the ordinary. No cars, no ordinary streets. Just water, bridges and palazzi. Couples feel transported before the ceremony begins.",
      differentiation: "Venice versus Lake Como: Como is more dramatic and more exclusive. Venice is more theatrical and more accessible. Venice suits couples who want their guests to experience something genuinely different.",
      register: "romantic-grand",
      style_affinity: ["romantic", "historic-ceremony", "urban-prestige", "intimate"],
      season_peak: ["april", "may", "september", "october"],
      season_avoid: ["july", "august"],
      scale_fit: "medium",
      not_for: ["rustic-farmhouse", "vineyard", "wild-remote"],
      price_index: "ultra",
      cross_sell: ["lake-como", "tuscany", "rome"],
    },
  },
  {
    id: "milan", countrySlug: "italy", slug: "milan", name: "Milan",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    lat: 45.465, lng: 9.186,
    canonicalRoute: "/italy/milan",
    description: "Italy's fashion and design capital, contemporary elegance, Michelin dining and world-class couture.",
    focusKeywords: ["milan wedding", "milan wedding venue", "luxury wedding milan"],
    aiSummary: "Fashion-forward market. Bridal couture hub. Strong corporate-luxury crossover. Urban hotel and villa venues.",
    intentSignals: {
      high: ["milan luxury wedding venue", "milan wedding planner"],
      mid: ["best milan wedding venues"],
      low: ["milan wedding ideas"],
    },
    entityAliases: ["Milano"],
  },
  {
    id: "italian-riviera", countrySlug: "italy", slug: "italian-riviera", name: "Italian Riviera",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    lat: 44.303, lng: 9.209,
    canonicalRoute: "/italy/italian-riviera",
    description: "Portofino's pastel harbour and Cinque Terre's terraced villages, Liguria's coastline at its most glamorous.",
    focusKeywords: ["italian riviera wedding", "portofino wedding venue", "cinque terre wedding"],
    aiSummary: "Ultra-exclusive micro-market. Portofino anchors luxury tier. Cinque Terre drives aspirational search.",
    intentSignals: {
      high: ["portofino wedding venue book", "italian riviera wedding planner"],
      mid: ["best italian riviera wedding venues"],
      low: ["portofino wedding ideas", "cinque terre wedding inspiration"],
    },
    entityAliases: ["Riviera Ligure", "Liguria Coast"],
    ai: {
      positioning: "Cinque Terre cliffs, olive terraces and the cerulean Ligurian sea, a coastline that defined what Mediterranean beauty means.",
      opening_line: "The Italian Riviera is vertical. The villages cling to cliffs, the gardens are terraced into the rock, and the sea sits below everything. Ceremonies here feel suspended between earth and water.",
      differentiation: "Italian Riviera versus Amalfi Coast: Both are dramatically coastal. The Riviera is slightly more accessible, slightly less extreme. Portofino has a village intimacy that Positano cannot match.",
      register: "dramatic-coastal",
      style_affinity: ["coastal-dramatic", "romantic", "historic-ceremony", "intimate"],
      season_peak: ["may", "june", "september"],
      season_avoid: ["july", "august"],
      scale_fit: "intimate",
      not_for: ["vineyard", "rustic-farmhouse", "urban-prestige", "wild-remote"],
      price_index: "premium",
      cross_sell: ["amalfi-coast", "tuscany", "lake-como"],
    },
  },
  {
    id: "dolomites", countrySlug: "italy", slug: "dolomites", name: "Dolomites",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    lat: 46.499, lng: 11.356,
    canonicalRoute: "/italy/dolomites",
    description: "UNESCO jagged peaks and alpine meadows, dramatic mountain ceremonies in northern Italy's most spectacular landscape.",
    focusKeywords: ["dolomites wedding", "cortina wedding venue", "mountain wedding italy"],
    aiSummary: "Emerging alpine luxury destination. Winter and summer dual season. Cortina d'Ampezzo leads.",
    intentSignals: {
      high: ["cortina wedding venue", "dolomites wedding planner"],
      mid: ["best dolomites wedding venues"],
      low: ["dolomites wedding ideas"],
    },
    entityAliases: ["Dolomiti"],
    ai: {
      positioning: "Dramatic Alpine scenery in the heart of Italy, vertical rock faces, pine forests, and a mountain silence that makes a wedding feel genuinely extraordinary.",
      opening_line: "The Dolomites change the scale of everything. Venues that would feel large anywhere else feel intimate against those peaks. Couples who want grandeur without opulence choose the mountains.",
      differentiation: "Dolomites versus Scottish Highlands: Both are wild and elevated. The Dolomites have better infrastructure, better food, and more reliable weather. The Highlands are wilder and more genuinely remote.",
      register: "wild-remote",
      style_affinity: ["wild-remote", "rustic-luxe", "intimate", "romantic"],
      season_peak: ["june", "july", "august", "september"],
      season_avoid: ["november", "december", "january", "february"],
      scale_fit: "intimate",
      not_for: ["urban-prestige", "vineyard", "coastal-dramatic", "historic-ceremony"],
      price_index: "mid",
      cross_sell: ["lake-como", "lake-garda", "tuscany"],
    },
  },
  {
    id: "lake-garda", countrySlug: "italy", slug: "lake-garda", name: "Lake Garda",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    lat: 45.618, lng: 10.663,
    canonicalRoute: "/italy/lake-garda",
    description: "Italy's largest lake, lemon groves, Sirmione's thermal peninsula and Gardone Riviera's belle époque villas.",
    focusKeywords: ["lake garda wedding", "lake garda wedding venue", "sirmione wedding"],
    aiSummary: "Growing market. Sirmione and Gardone Riviera lead. Lower price point than Lake Como. Strong German/Austrian demand.",
    intentSignals: {
      high: ["lake garda wedding venue book"],
      mid: ["best lake garda wedding venues"],
      low: ["lake garda wedding ideas"],
    },
    entityAliases: ["Lago di Garda", "Benaco"],
    ai: {
      positioning: "Italy's largest lake, operatic scenery, accessible from Milan and Verona, with more venue variety than Como at a fraction of the price.",
      opening_line: "Lake Garda does not have Como's prestige, but it has something Como lacks: scale. Larger venues, more suppliers, more availability, and a sense that guests can actually get here.",
      differentiation: "Lake Garda versus Lake Como: Como is more exclusive and more expensive. Garda has more venue variety and better logistics. Couples who want the lake aesthetic without the Como premium choose Garda.",
      register: "romantic-grand",
      style_affinity: ["lakeside", "romantic", "historic-ceremony", "rustic-luxe"],
      season_peak: ["may", "june", "september"],
      season_avoid: ["november", "december", "january", "february"],
      scale_fit: "all",
      not_for: ["wild-remote", "coastal-dramatic"],
      price_index: "mid",
      cross_sell: ["lake-como", "tuscany", "venice"],
    },
  },
  {
    id: "lake-maggiore", countrySlug: "italy", slug: "lake-maggiore", name: "Lake Maggiore",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    lat: 45.935, lng: 8.568,
    canonicalRoute: "/italy/lake-maggiore",
    description: "Borromean Islands, Stresa's grand hotels and a gentler, more intimate lake than Como.",
    focusKeywords: ["lake maggiore wedding", "stresa wedding venue", "isola bella wedding"],
    aiSummary: "Niche lake market. Borromean Islands are unique draw. Stresa grand hotel circuit. Less crowded than Como.",
    intentSignals: {
      high: ["lake maggiore wedding venue"],
      mid: ["best lake maggiore wedding venues"],
      low: ["lake maggiore wedding ideas"],
    },
    entityAliases: ["Lago Maggiore", "Verbano"],
  },
  {
    id: "capri", countrySlug: "italy", slug: "capri", name: "Capri",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    lat: 40.550, lng: 14.223,
    canonicalRoute: "/italy/capri",
    description: "The legendary isle of la dolce vita, Faraglioni rocks, the Blue Grotto and Caprese glamour since the Roman emperors.",
    focusKeywords: ["capri wedding", "capri wedding venue", "island wedding capri"],
    aiSummary: "Ultra-luxury island market. Hotel-only venue model. Strong celebrity and fashion association.",
    intentSignals: {
      high: ["capri wedding venue book"],
      mid: ["best capri wedding venues"],
      low: ["capri wedding ideas"],
    },
    entityAliases: ["Isola di Capri"],
  },
  {
    id: "ischia", countrySlug: "italy", slug: "ischia", name: "Ischia",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    lat: 40.731, lng: 13.897,
    canonicalRoute: "/italy/ischia",
    description: "Volcanic thermal springs, lush gardens and a quieter, more authentic Gulf of Naples island experience.",
    focusKeywords: ["ischia wedding", "ischia wedding venue"],
    aiSummary: "Emerging alternative to Capri. Thermal wellness crossover. Lower price point. Growing international awareness.",
    intentSignals: {
      high: [],
      mid: ["ischia wedding venues"],
      low: ["ischia wedding ideas"],
    },
    entityAliases: ["Isola d'Ischia"],
  },
  {
    id: "sardinia", countrySlug: "italy", slug: "sardinia", name: "Sardinia",
    priorityLevel: "primary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    lat: 40.923, lng: 9.503,
    canonicalRoute: "/italy/sardinia",
    description: "Costa Smeralda's emerald bays, Porto Cervo's superyacht culture and wild interior landscapes.",
    focusKeywords: ["sardinia wedding", "costa smeralda wedding venue", "sardinia destination wedding"],
    aiSummary: "Ultra-luxury beach resort market. Costa Smeralda drives demand. Strong European summer season.",
    intentSignals: {
      high: ["costa smeralda wedding venue book", "sardinia wedding planner"],
      mid: ["best sardinia wedding venues"],
      low: ["sardinia wedding ideas"],
    },
    entityAliases: ["Sardegna"],
    ai: {
      positioning: "Turquoise water that has no right to look like that, and a northern coast where the villas are serious and the privacy is total.",
      opening_line: "Sardinia delivers the Mediterranean at its most unspoiled. The Costa Smeralda is the right address for couples who want the sea and also require something closer to a private island.",
      differentiation: "Sardinia versus Sicily: Sicily has more culture and more complexity. Sardinia has the better sea and the greater sense of escape. Couples who have already done the culture choose Sardinia.",
      register: "dramatic-coastal",
      style_affinity: ["coastal-dramatic", "intimate", "sun-social", "romantic"],
      season_peak: ["june", "july", "august", "september"],
      season_avoid: ["november", "december", "january", "february"],
      scale_fit: "intimate",
      not_for: ["historic-ceremony", "urban-prestige", "vineyard", "wild-remote"],
      price_index: "ultra",
      cross_sell: ["sicily", "amalfi-coast", "puglia"],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SECONDARY REGIONS
  // ═══════════════════════════════════════════════════════════════════════════

  {
    id: "umbria", countrySlug: "italy", slug: "umbria", name: "Umbria",
    priorityLevel: "secondary", urlEnabledManual: null, urlEverActivated: false, listingCount: 2,
    lat: 42.954, lng: 12.355,
    canonicalRoute: "/italy/umbria",
    description: "The green heart of Italy, medieval villages, olive groves and understated elegance.",
    focusKeywords: ["umbria wedding", "umbria wedding venue"],
    aiSummary: "Niche market. Overflow from Tuscany demand. Growing slowly.",
    intentSignals: { high: [], mid: ["umbria wedding venues"], low: ["umbria wedding ideas"] },
    entityAliases: ["L'Umbria"],
  },
  {
    id: "marche", countrySlug: "italy", slug: "marche", name: "Marche",
    priorityLevel: "secondary", urlEnabledManual: null, urlEverActivated: false, listingCount: 1,
    lat: 43.616, lng: 13.509,
    canonicalRoute: "/italy/marche",
    description: "Adriatic coastline meets rolling countryside. An undiscovered gem for intimate weddings.",
    focusKeywords: ["marche wedding"],
    aiSummary: "Very early stage. Minimal search volume. Monitor for growth.",
    intentSignals: { high: [], mid: [], low: ["marche wedding ideas"] },
    entityAliases: ["Le Marche"],
  },
  {
    id: "abruzzo", countrySlug: "italy", slug: "abruzzo", name: "Abruzzo",
    priorityLevel: "secondary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    canonicalRoute: "/italy/abruzzo",
    description: "Mountain scenery and medieval borghi. Wild, dramatic and virtually untouched by wedding tourism.",
    focusKeywords: ["abruzzo wedding"],
    aiSummary: "Pre-market. No active listings. Watchlist only.",
    intentSignals: { high: [], mid: [], low: ["abruzzo wedding"] },
    entityAliases: ["L'Abruzzo"],
  },
  {
    id: "piedmont", countrySlug: "italy", slug: "piedmont", name: "Piedmont",
    priorityLevel: "secondary", urlEnabledManual: null, urlEverActivated: false, listingCount: 1,
    lat: 45.054, lng: 7.687,
    canonicalRoute: "/italy/piedmont",
    description: "Barolo vineyards, Alba truffles and the alpine grandeur of Italy's northwest, refined, understated luxury.",
    focusKeywords: ["piedmont wedding", "piedmont wedding venue", "barolo wedding"],
    aiSummary: "Wine tourism crossover. Langhe UNESCO vineyards. Michelin dining density. Niche luxury market.",
    intentSignals: {
      high: [],
      mid: ["piedmont wedding venues", "langhe wedding"],
      low: ["piedmont wedding ideas"],
    },
    entityAliases: ["Piemonte"],
  },
  {
    id: "emilia-romagna", countrySlug: "italy", slug: "emilia-romagna", name: "Emilia-Romagna",
    priorityLevel: "secondary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    canonicalRoute: "/italy/emilia-romagna",
    description: "Italy's culinary heartland, Parma, Bologna, Modena and the Romagna Riviera.",
    focusKeywords: ["emilia romagna wedding"],
    aiSummary: "Food tourism crossover potential. Minimal wedding-specific search. Monitor.",
    intentSignals: { high: [], mid: [], low: ["emilia romagna wedding"] },
    entityAliases: ["Emilia Romagna"],
  },
  {
    id: "calabria", countrySlug: "italy", slug: "calabria", name: "Calabria",
    priorityLevel: "secondary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    canonicalRoute: "/italy/calabria",
    description: "The toe of Italy's boot, wild coastline, ancient Greek ruins and authentic southern hospitality.",
    focusKeywords: ["calabria wedding"],
    aiSummary: "Pre-market. Very low search volume. Tropea coast has potential. Watchlist.",
    intentSignals: { high: [], mid: [], low: ["calabria wedding"] },
    entityAliases: ["La Calabria"],
  },
  {
    id: "basilicata", countrySlug: "italy", slug: "basilicata", name: "Basilicata",
    priorityLevel: "secondary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    canonicalRoute: "/italy/basilicata",
    description: "Matera's cave dwellings and dramatic ravines, UNESCO World Heritage and an utterly unique ceremony backdrop.",
    focusKeywords: ["basilicata wedding", "matera wedding venue"],
    aiSummary: "Matera drives all search. Cave hotel weddings unique globally. Very niche.",
    intentSignals: { high: [], mid: ["matera wedding venue"], low: ["basilicata wedding"] },
    entityAliases: ["Lucania"],
  },
  {
    id: "trentino-alto-adige", countrySlug: "italy", slug: "trentino-alto-adige", name: "Trentino-Alto Adige",
    priorityLevel: "secondary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    canonicalRoute: "/italy/trentino-alto-adige",
    description: "Alpine valleys, South Tyrolean castles and a unique blend of Italian and Austrian culture.",
    focusKeywords: ["trentino wedding", "south tyrol wedding venue"],
    aiSummary: "Alpine luxury niche. Overlap with Dolomites region. Austrian-Italian dual culture unique.",
    intentSignals: { high: [], mid: [], low: ["trentino wedding", "south tyrol wedding"] },
    entityAliases: ["Trentino-Südtirol", "Trentino"],
  },
  {
    id: "friuli-venezia-giulia", countrySlug: "italy", slug: "friuli-venezia-giulia", name: "Friuli Venezia Giulia",
    priorityLevel: "secondary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    canonicalRoute: "/italy/friuli-venezia-giulia",
    description: "Trieste's Habsburg elegance, Collio vineyards and the wild Dolomiti Friulane mountains.",
    focusKeywords: ["friuli wedding"],
    aiSummary: "Very niche. Trieste has cultural appeal. Collio wine region growing. Low volume.",
    intentSignals: { high: [], mid: [], low: ["friuli wedding"] },
    entityAliases: ["Friuli", "FVG"],
  },
  {
    id: "valle-d-aosta", countrySlug: "italy", slug: "valle-d-aosta", name: "Valle d'Aosta",
    priorityLevel: "secondary", urlEnabledManual: null, urlEverActivated: false, listingCount: 0,
    canonicalRoute: "/italy/valle-d-aosta",
    description: "Italy's smallest region, Mont Blanc, medieval castles and intimate Alpine valley celebrations.",
    focusKeywords: ["valle d'aosta wedding"],
    aiSummary: "Micro-market. Castle weddings potential. Mont Blanc backdrop unique. Very early.",
    intentSignals: { high: [], mid: [], low: ["valle d'aosta wedding"] },
    entityAliases: ["Val d'Aosta", "Vallée d'Aoste"],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // LEGACY REGIONS, from imported listing data, disabled by default.
  // These exist for traceability. Listings from these regions have been mapped
  // to spec regions where the city clearly belongs (e.g. Ravello → amalfi-coast).
  // Listings that cannot be cleanly mapped retain regionSlug of the legacy region.
  // ═══════════════════════════════════════════════════════════════════════════

  {
    id: "veneto", countrySlug: "italy", slug: "veneto", name: "Veneto",
    priorityLevel: "secondary", urlEnabledManual: false, urlEverActivated: false, listingCount: 0,
    canonicalRoute: "/italy/veneto",
    description: "The Veneto extends far beyond Venice, Verona, Padua, the Prosecco hills and the Palladian villas of the Brenta.",
    focusKeywords: ["veneto wedding"],
    aiSummary: "Legacy region. Venice listings mapped to venice region. Residual Veneto-only listings may exist.",
    intentSignals: { high: [], mid: [], low: ["veneto wedding"] },
    entityAliases: ["Il Veneto"],
    isLegacy: true,
    legacyNote: "Legacy region from imported listings. Venice-area listings mapped to venice region.",
  },
  {
    id: "lombardy", countrySlug: "italy", slug: "lombardy", name: "Lombardy",
    priorityLevel: "secondary", urlEnabledManual: false, urlEverActivated: false, listingCount: 1,
    canonicalRoute: "/italy/lombardy",
    description: "Italy's economic heartland, Milan, Lake Como, Lake Garda and the Lombard plain.",
    focusKeywords: ["lombardy wedding"],
    aiSummary: "Legacy region. Lake Como and Milan listings mapped to their own regions. Non-lake Lombard listings remain here.",
    intentSignals: { high: [], mid: [], low: ["lombardy wedding"] },
    entityAliases: ["Lombardia"],
    isLegacy: true,
    legacyNote: "Legacy region from imported listings. Lake Como / Milan listings mapped to lake-como and milan regions.",
  },
  {
    id: "lazio", countrySlug: "italy", slug: "lazio", name: "Lazio",
    priorityLevel: "secondary", urlEnabledManual: false, urlEverActivated: false, listingCount: 0,
    canonicalRoute: "/italy/lazio",
    description: "The region surrounding Rome, ancient Etruscan villages, volcanic lakes and the Roman countryside.",
    focusKeywords: ["lazio wedding"],
    aiSummary: "Legacy region. Rome-area listings mapped to rome region. Non-Rome Lazio listings may exist.",
    intentSignals: { high: [], mid: [], low: ["lazio wedding"] },
    entityAliases: ["Il Lazio", "Latium"],
    isLegacy: true,
    legacyNote: "Legacy region from imported listings. Rome-area listings mapped to rome region.",
  },
  {
    id: "campania", countrySlug: "italy", slug: "campania", name: "Campania",
    priorityLevel: "secondary", urlEnabledManual: false, urlEverActivated: false, listingCount: 0,
    canonicalRoute: "/italy/campania",
    description: "Naples, Pompeii, the Amalfi Coast and the islands of Capri and Ischia, southern Italy's most celebrated coastline.",
    focusKeywords: ["campania wedding"],
    aiSummary: "Legacy region. Amalfi Coast / Capri / Ischia listings mapped to their own regions.",
    intentSignals: { high: [], mid: [], low: ["campania wedding"] },
    entityAliases: ["La Campania"],
    isLegacy: true,
    legacyNote: "Legacy region from imported listings. Amalfi Coast listings mapped to amalfi-coast region.",
  },
];
